import type { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import type { ReadonlyURLSearchParams } from "next/navigation";
import type { QueryBarState, ViewType } from "../query/types";

export type LayoutSectionUrl = {
  field: string;
  groupKey?: string;
};

export type LayoutCalendarUrl = {
  startField?: string | null;
  endField?: string | null;
  recurrenceField?: string | null;
  seriesIdField?: string | null;
  originalStartField?: string | null;
  mode?: "day" | "week" | "month";
  weekdays?: Array<"sun" | "mon" | "tue" | "wed" | "thu" | "fri" | "sat">;
  startTime?: string | null;
  endTime?: string | null;
  stepMinutes?: number | null;
  sectionField?: string | null;
  sectionGroupingKey?: string | null;
};

export type LayoutUrlState = {
  view?: ViewType;
  columns?: string[];
  kanbanStatus?: string | null;
  kanbanDescription?: string | null;
  section?: LayoutSectionUrl | null;
  subsection?: LayoutSectionUrl | null;
  calendar?: LayoutCalendarUrl;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

const parseLayoutSection = (value: unknown): LayoutSectionUrl | null => {
  if (!isPlainObject(value)) return null;
  if (typeof value.field !== "string" || value.field.length === 0) return null;
  return {
    field: value.field,
    groupKey:
      typeof value.groupKey === "string" ? value.groupKey : undefined,
  };
};

const CALENDAR_WEEKDAYS = [
  "sun",
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
] as const;

type CalendarWeekday = (typeof CALENDAR_WEEKDAYS)[number];

const parseLayoutCalendar = (value: unknown): LayoutCalendarUrl | undefined => {
  if (!isPlainObject(value)) return undefined;
  const weekdays = Array.isArray(value.weekdays)
    ? value.weekdays.filter((day): day is CalendarWeekday =>
        CALENDAR_WEEKDAYS.includes(day as CalendarWeekday),
      )
    : undefined;

  const calendar: LayoutCalendarUrl = {};
  if (typeof value.startField === "string") {
    calendar.startField = value.startField;
  } else if (value.startField === null) {
    calendar.startField = null;
  }
  if (typeof value.endField === "string") {
    calendar.endField = value.endField;
  } else if (value.endField === null) {
    calendar.endField = null;
  }
  if (typeof value.recurrenceField === "string") {
    calendar.recurrenceField = value.recurrenceField;
  } else if (value.recurrenceField === null) {
    calendar.recurrenceField = null;
  }
  if (typeof value.seriesIdField === "string") {
    calendar.seriesIdField = value.seriesIdField;
  } else if (value.seriesIdField === null) {
    calendar.seriesIdField = null;
  }
  if (typeof value.originalStartField === "string") {
    calendar.originalStartField = value.originalStartField;
  } else if (value.originalStartField === null) {
    calendar.originalStartField = null;
  }
  if (
    value.mode === "day" ||
    value.mode === "week" ||
    value.mode === "month"
  ) {
    calendar.mode = value.mode;
  }
  if (weekdays && weekdays.length > 0) {
    calendar.weekdays = weekdays;
  }
  if (typeof value.startTime === "string") {
    calendar.startTime = value.startTime;
  } else if (value.startTime === null) {
    calendar.startTime = null;
  }
  if (typeof value.endTime === "string") {
    calendar.endTime = value.endTime;
  } else if (value.endTime === null) {
    calendar.endTime = null;
  }
  if (typeof value.stepMinutes === "number" && Number.isFinite(value.stepMinutes)) {
    calendar.stepMinutes = value.stepMinutes;
  } else if (value.stepMinutes === null) {
    calendar.stepMinutes = null;
  }
  if (typeof value.sectionField === "string") {
    calendar.sectionField = value.sectionField;
  } else if (value.sectionField === null) {
    calendar.sectionField = null;
  }
  if (typeof value.sectionGroupingKey === "string") {
    calendar.sectionGroupingKey = value.sectionGroupingKey;
  } else if (value.sectionGroupingKey === null) {
    calendar.sectionGroupingKey = null;
  }

  return Object.keys(calendar).length > 0 ? calendar : undefined;
};

export const searchParamsToLayoutState = (
  params: ReadonlyURLSearchParams,
): LayoutUrlState | undefined => {
  const raw = params.get("layout");
  if (!raw) return undefined;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isPlainObject(parsed)) return undefined;

    const layout: LayoutUrlState = {};
    if (
      parsed.view === "spreadsheet" ||
      parsed.view === "kanban" ||
      parsed.view === "calendar"
    ) {
      layout.view = parsed.view;
    }
    if (Array.isArray(parsed.columns)) {
      layout.columns = parsed.columns.filter(
        (value): value is string => typeof value === "string" && value.length > 0,
      );
    }
    if ("kanbanStatus" in parsed) {
      layout.kanbanStatus =
        typeof parsed.kanbanStatus === "string" ? parsed.kanbanStatus : null;
    }
    if ("kanbanDescription" in parsed) {
      layout.kanbanDescription =
        typeof parsed.kanbanDescription === "string"
          ? parsed.kanbanDescription
          : null;
    }
    if ("section" in parsed) {
      layout.section =
        parsed.section === null ? null : parseLayoutSection(parsed.section);
    }
    if ("subsection" in parsed) {
      layout.subsection =
        parsed.subsection === null
          ? null
          : parseLayoutSection(parsed.subsection);
    }
    const calendar = parseLayoutCalendar(parsed.calendar);
    if (calendar) {
      layout.calendar = calendar;
    }

    return isEmptyLayoutState(layout) ? undefined : layout;
  } catch {
    return undefined;
  }
};

export const isEmptyLayoutState = (layout: LayoutUrlState | undefined): boolean => {
  if (!layout) return true;
  return (
    !layout.view &&
    (!layout.columns || layout.columns.length === 0) &&
    !("kanbanStatus" in layout) &&
    !("kanbanDescription" in layout) &&
    !("section" in layout) &&
    !("subsection" in layout) &&
    !layout.calendar
  );
};

export const buildLayoutUrlStateFromQueryBarState = (
  state: QueryBarState,
): LayoutUrlState => {
  const layout: LayoutUrlState = {};

  if (state.view) {
    layout.view = state.view;
  }

  const columns =
    state.layoutSelectedFieldValues && state.layoutSelectedFieldValues.length > 0
      ? state.layoutSelectedFieldValues
      : state.selectedFields.map((field) => field.value);
  if (columns.length > 0) {
    layout.columns = columns;
  }

  if (state.kanbanStatusBadgeField !== undefined) {
    layout.kanbanStatus = state.kanbanStatusBadgeField?.value ?? null;
  }
  if (state.kanbanDescriptionField !== undefined) {
    layout.kanbanDescription = state.kanbanDescriptionField?.value ?? null;
  }

  if (state.section !== undefined) {
    layout.section = state.section?.field
      ? {
          field: state.section.field.value,
          groupKey: state.section.groupKey || "",
        }
      : null;
  }
  if (state.subsection !== undefined) {
    layout.subsection = state.subsection?.field
      ? {
          field: state.subsection.field.value,
          groupKey: state.subsection.groupKey || "",
        }
      : null;
  }

  const hasCalendarConfig =
    state.view === "calendar" ||
    state.calendarStartField ||
    state.calendarEndField ||
    state.calendarRecurrenceField ||
    state.calendarSeriesIdField ||
    state.calendarOriginalStartField ||
    state.calendarMode ||
    state.calendarWeekdays ||
    state.calendarStartTime ||
    state.calendarEndTime ||
    state.calendarStepMinutes != null;

  if (hasCalendarConfig) {
    layout.calendar = {
      startField: state.calendarStartField?.value ?? null,
      endField: state.calendarEndField?.value ?? null,
      recurrenceField: state.calendarRecurrenceField?.value ?? null,
      seriesIdField: state.calendarSeriesIdField?.value ?? null,
      originalStartField: state.calendarOriginalStartField?.value ?? null,
      mode: state.calendarMode,
      weekdays: state.calendarWeekdays,
      startTime: state.calendarStartTime ?? null,
      endTime: state.calendarEndTime ?? null,
      stepMinutes: state.calendarStepMinutes ?? null,
      sectionField: state.section?.field?.value ?? null,
      sectionGroupingKey: state.section?.groupKey ?? null,
    };
  }

  return layout;
};

export const buildSearchStringWithLayout = (
  existing: ReadonlyURLSearchParams,
  layout: LayoutUrlState | undefined,
): string => {
  const params = new URLSearchParams(existing.toString());

  if (isEmptyLayoutState(layout)) {
    params.delete("layout");
  } else {
    params.set("layout", JSON.stringify(layout));
  }

  return params.toString();
};

export const applyLayoutToUrl = (
  router: AppRouterInstance,
  pathname: string,
  existing: ReadonlyURLSearchParams,
  layout: LayoutUrlState | undefined,
): void => {
  const search = buildSearchStringWithLayout(existing, layout);
  const url = search ? `${pathname}?${search}` : pathname;
  router.push(url);
};

export const getUrlLayout = (
  searchParams: ReadonlyURLSearchParams,
): LayoutUrlState | undefined => searchParamsToLayoutState(searchParams);
